import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import useFetch from 'use-http';
import { useSnackbar } from 'notistack';

// Actions
import { getBillingData, getStore, setBillingsData } from '../../actions/auth';

// Components
import BillingsButton from '../../components/Billings/BillingsButton/BillingsButton';
import BillingsTable from '../../components/Billings/BillingsTable/BillingsTable';
import BillingProfileForm from '../../components/Billings/BillingProfile/BillingProfileForm';
import ChangeCUIL from '../../components/Modals/ChangeCUIL/ChangeCUIL';

// Configurations
import { MAIN_API_URL } from '../../configuration';

// Layout
import LayoutPages from '../../layout/LayoutPages';

// Material Ui
import { Box, Tooltip } from '@mui/material';

// PaymentProvider
import { ProviderPaymentList } from '../../components/Billings/BillingsTable/ProviderPaymentList';

// Kit
import {
    PageHeader,
    Card,
    Badge,
    Button,
    Alert,
    AlertTitle,
    AlertDescription,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from '@/components/ui';

// Icons
import { CreditCard, FileText, Pencil, AlertTriangle } from 'lucide-react';

const BillingScreenContainer = {
    userSelect: 'none',
    textAlign: 'left'
}

// Fila "dato al vistazo" del resumen fiscal: etiqueta tenue + valor destacado.
const SummaryRow = ({ label, value }) => (
    <div className="flex items-baseline justify-between gap-3">
        <dt className="shrink-0 text-xs font-medium text-muted-foreground">{label}</dt>
        <dd className="min-w-0 break-words text-right text-sm font-semibold text-foreground" title={value || undefined}>
            {value || '—'}
        </dd>
    </div>
);

const BillingScreen = (props) => {

    // Actions
    const { setBillingsData, getBillingData, getStore, store } = props;

    const [showCuilModal, setShowCuilModal] = useState(false);
    const [activeTab, setActiveTab] = useState('facturas');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [fourNumber, setFourNumber] = useState('');
    const [userData, setUserData] = useState([]);
    const [paymentData, setPaymentData] = useState({
        paymentMethod: '',
        subscriptionLink: '',
    });
    const [icon, setIcon] = useState('');
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [billingProfile, setBillingProfile] = useState({});
    const [billingCompleteness, setBillingCompleteness] = useState({ ok: false, missing: [] });
    const perPage = 12;

    const { enqueueSnackbar } = useSnackbar();

    const { error, response, get } = useFetch(`${MAIN_API_URL}`, {
        headers: {
            "x-token": `${localStorage.getItem("token")}`
        }
    });

    useEffect(() => { getTableData(); }, [page]);
    useEffect(() => { getUserData(); getBillingProfile(); }, []);

    const getTableData = async () => {
        const resp = await get(`/invoices?page=${page + 1}&perPage=${perPage}`);
        if (response.ok) {
            setRows(resp.invoices || []);
            setTotal(resp.total || 0);
        } else {
            console.log(error);
        }
    }

    const getUserData = async () => {
        const resp = await get("/stores");
        if (response.ok) {
            setUserData(resp);
        } else {
            console.log(error);
        }
    }

    const getBillingProfile = async () => {
        const resp = await get('/billing/profile');
        if (response.ok && resp) {
            setBillingProfile(resp.profile || {});
            setBillingCompleteness(resp.completeness || { ok: false, missing: [] });
        }
    }

    const saveBillingProfile = async (profile) => {
        try {
            const resp = await fetch(`${MAIN_API_URL}/billing/profile`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'x-token': `${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(profile),
            });
            const data = await resp.json();
            if (!resp.ok) {
                const serverMsg = Array.isArray(data.errors) && data.errors.length
                    ? (data.errors[0].msg || data.errors[0])
                    : (data.msg || 'Error guardando datos fiscales');
                enqueueSnackbar(serverMsg, { variant: 'error' });
                return;
            }
            setBillingProfile(data.profile || {});
            setBillingCompleteness(data.completeness || { ok: true, missing: [] });
            enqueueSnackbar('Datos de facturación guardados', { variant: 'success' });
            getTableData();
        } catch (e) {
            enqueueSnackbar('Error de conexión al guardar datos fiscales', { variant: 'error' });
        }
    }

    const lookupAfip = async (cuit) => {
        try {
            const resp = await fetch(`${MAIN_API_URL}/billing/profile/afip-lookup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-token': `${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({ cuit }),
            });
            if (!resp.ok) {
                enqueueSnackbar('No pudimos encontrar el CUIT en ARCA', { variant: 'warning' });
                return null;
            }
            const data = await resp.json();
            return data.profile || data.data;
        } catch (e) {
            enqueueSnackbar('Error consultando ARCA', { variant: 'error' });
            return null;
        }
    }

    useEffect(() => {
        async function getSuscriptionData() {
            if (store && store.suscriptionData) {
                if (store.mainLanguage === 'es') {
                    const resp = await get(`/subscriptions/mercadopago`);
                    if (response.ok) {
                        const { data } = resp;
                        setPaymentData({
                            paymentMethod: data.payment_method_id,
                            subscriptionLink: `https://www.mercadopago.com.ar/subscriptions/v0/${data.id}/admin`
                        });
                        for (let j = 0; j < ProviderPaymentList.length; j++) {
                            if (data.payment_method_id === ProviderPaymentList[j].code) {
                                setPaymentMethod(ProviderPaymentList[j].name);
                                setIcon(ProviderPaymentList[j].icon);
                            }
                        }

                    }
                } else {
                    const resp = await get(`/subscriptions/stripe`);
                    if (response.ok) {
                        console.log(resp);
                    }
                }
            }
        }
        getSuscriptionData();
    }, [store]);

    const handleChangeData = () => {
        setShowCuilModal(!showCuilModal);
    }

    const handleChangeCardData = () => {
        console.log('Change card data');
    }

    useEffect(() => {
        getBillingData();
        const token = localStorage.getItem('token');
        getStore(token);
    }, []);

    // Facturas bloqueadas por datos fiscales incompletos (misma regla que la del form).
    const blockedCount = (rows || []).filter((row) => row.fiscal && row.fiscal.status === 'BLOCKED_MISSING_DATA').length;

    // Resumen fiscal "al vistazo" para la Zona 1.
    const profileDocType = billingProfile?.docType || 'CUIT';
    const profileDocNumber = billingProfile?.docNumber;
    const profileBusinessName = billingProfile?.businessName;
    const profileTaxCondition = billingProfile?.taxCondition;
    const hasProfileData = Boolean(profileBusinessName || profileDocNumber);
    const profileComplete = Boolean(billingCompleteness && billingCompleteness.ok);

    return (
        <LayoutPages>
            <Box sx={BillingScreenContainer} >
                <PageHeader
                    eyebrow="Facturación"
                    title="Facturación"
                    subtitle="Gestioná tus datos fiscales, revisá tu medio de pago y descargá tus comprobantes."
                />

                {/* Zona 1 — Resumen (siempre visible) */}
                <div className="mb-6 space-y-4">
                    {blockedCount > 0 && (
                        <Alert variant="warning">
                            <AlertTriangle />
                            <AlertTitle>Datos fiscales incompletos</AlertTitle>
                            <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <span>Completá tus datos fiscales para recibir tu factura. Tenés {blockedCount} facturas esperando.</span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="shrink-0"
                                    onClick={() => setActiveTab('datos')}
                                >
                                    Completar datos
                                </Button>
                            </AlertDescription>
                        </Alert>
                    )}

                    <section className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2">
                        {/* Medio de pago actual */}
                        <Card className="flex min-h-full flex-col p-6 text-left">
                            <div className="flex items-center gap-2.5">
                                <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-tint text-primary">
                                    <CreditCard className="size-4" />
                                </span>
                                <Tooltip title='En caso de haberlo modificado en el último mes, podrás visualizarlo directamente desde el procesador de pagos' arrow placement='left'>
                                    <h2 className="w-fit cursor-help text-lg font-bold leading-tight tracking-[-0.01em] text-foreground">Medio de pago actual</h2>
                                </Tooltip>
                            </div>
                            <div className="mt-4 flex-1">
                                {store && store.suscriptionData
                                    ? <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                                        {icon}
                                        <p className="m-0 text-base font-semibold text-foreground">{paymentMethod}</p>
                                        {paymentData.subscriptionLink && <a className="text-sm font-semibold text-primary hover:underline" href={paymentData.subscriptionLink} target='_blank' rel='noreferrer' >Ver suscripción</a>}
                                    </div>
                                    : <p className="m-0 text-sm text-muted-foreground">No hay información de medios de pago</p>
                                }
                            </div>
                            <div className="mt-6 flex justify-end">
                                <BillingsButton
                                    handleClick={handleChangeCardData}
                                    text={(store && store.suscriptionData) ? 'Cambiar medio de pago' : 'Suscribirme'}
                                    type={'Card'}
                                />
                            </div>
                        </Card>

                        {/* Datos de facturación (resumen) */}
                        <Card className="flex min-h-full flex-col p-6 text-left">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2.5">
                                    <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-tint text-primary">
                                        <FileText className="size-4" />
                                    </span>
                                    <h2 className="text-lg font-bold leading-tight tracking-[-0.01em] text-foreground">Datos de facturación</h2>
                                </div>
                                <Badge variant={profileComplete ? 'success' : 'warning'} size="sm">
                                    {profileComplete ? 'Completo' : 'Incompleto'}
                                </Badge>
                            </div>
                            <div className="mt-4 flex-1">
                                {hasProfileData
                                    ? <dl className="space-y-2.5">
                                        <SummaryRow label="Razón social" value={profileBusinessName} />
                                        <SummaryRow label={profileDocType} value={profileDocNumber} />
                                        <SummaryRow label="Condición IVA" value={profileTaxCondition} />
                                    </dl>
                                    : <p className="m-0 text-sm text-muted-foreground">Todavía no cargaste tus datos fiscales.</p>
                                }
                            </div>
                            <div className="mt-6 flex justify-end">
                                <Button variant="outline" onClick={() => setActiveTab('datos')}>
                                    <Pencil /> Editar
                                </Button>
                            </div>
                        </Card>
                    </section>
                </div>

                {/* Zona 2 — Detalle en tabs (Facturas por defecto) */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="facturas">Facturas</TabsTrigger>
                        <TabsTrigger value="datos">Datos de facturación</TabsTrigger>
                    </TabsList>

                    <TabsContent value="facturas">
                        <BillingsTable
                            rows={rows}
                            total={total}
                            page={page}
                            perPage={perPage}
                            onPageChange={setPage}
                        />
                    </TabsContent>

                    <TabsContent value="datos">
                        <BillingProfileForm
                            profile={billingProfile}
                            completeness={billingCompleteness}
                            onSave={saveBillingProfile}
                            onAfipLookup={lookupAfip}
                            blockedCount={blockedCount}
                        />
                    </TabsContent>
                </Tabs>

                <ChangeCUIL
                    open={showCuilModal}
                    handleClose={handleChangeData}
                    cuilt={userData.billingData?.document_nro}
                    email={userData.billingData?.email}
                />
            </Box>
        </LayoutPages>
    )
}

const mapStateToProps = state => {
    return {
        store: state.auth.store,
    }
}

const mapDispatchToProps = dispatch => {
    return {
        setBillingsData: (value) => dispatch(setBillingsData(value)),
        getBillingData: () => dispatch(getBillingData()),
        getStore: () => dispatch(getStore()),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(BillingScreen);
